import { Loader2 } from "lucide-react";
import { useState } from "react";
import { FormProvider, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/hooks/use-mobile";
import { ErrorAlert } from "@/lib/components/error-alert";
import { BackButton, BaseNavigationBar } from "@/lib/components/navigation";
import { FormActionButtons, NavigationGuardProvider } from "@/lib/components/navigation-guard";
import { PageContent } from "@/lib/components/page-content";
import { SettingsSearchTrigger } from "../../search/components/settings-search-trigger";
import {
  BasicLimitsConfirmDialog,
  CapacityModeSelector,
  DailyLimitField,
  DurationAwareToggle,
  SimpleCapacityForm,
  TimeRangeLimits,
} from "./components";
import type { BasicLimits } from "./components/basic-limits-confirm-dialog";
import { useCapacitySettings } from "./hooks/use-capacity-settings";

export function LimitsSettingsPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t } = useTranslation("settings");
  const { form, loading, saving, save, discard, handleModeChange, error } = useCapacitySettings();

  // Watch capacity mode using useWatch for React 19 + compiler optimization
  const capacityMode = useWatch({ control: form.control, name: "capacityMode" });
  // Captured on Save so the dialog shows exactly what will be written; the
  // dialog is open while this is set.
  const [confirmLimits, setConfirmLimits] = useState<BasicLimits | null>(null);

  const requestSave = async () => {
    if (capacityMode === "simple") {
      const valid = await form.trigger();
      if (!valid) return;
      const [concurrentMax, dailyMax, durationAware] = form.getValues([
        "concurrentMax",
        "dailyMax",
        "durationAware",
      ]);
      setConfirmLimits({ concurrentMax, dailyMax, durationAware });
      return;
    }
    const valid = await form.trigger();
    if (!valid) return;
    return save();
  };

  const guardSave = async () => {
    const valid = await form.trigger();
    if (!valid) throw new Error("Validation failed");
    return save();
  };

  const modeSelector = (
    <div id="field-limits-capacity-mode">
      <CapacityModeSelector value={capacityMode} onChange={handleModeChange} disabled={saving} />
    </div>
  );

  if (loading) {
    return (
      <>
        <BaseNavigationBar
          title={t("sections.limits.title")}
          subtitle={t("sections.limits.description")}
          leadElement={
            isMobile ? <BackButton key="back-button" onBack={() => navigate(-1)} /> : undefined
          }
        />

        <PageContent>
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8 flex items-center justify-center">
              <div className="flex items-center space-x-3">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                <span className="text-lg">{t("common.status.loading")}</span>
              </div>
            </CardContent>
          </Card>
        </PageContent>
      </>
    );
  }

  return (
    <NavigationGuardProvider
      hasUnsavedChanges={form.formState.isDirty}
      onSave={guardSave}
      onDiscard={discard}
      saving={saving}
      description={t("messages.unsavedChanges")}
    >
      <BaseNavigationBar
        title={t("sections.limits.title")}
        subtitle={t("sections.limits.description")}
        leadElement={
          isMobile ? <BackButton key="back-button" onBack={() => navigate(-1)} /> : undefined
        }
        trailingElements={[
          <SettingsSearchTrigger key="search-trigger" />,
          <FormActionButtons
            key="form-actions"
            isDirty={form.formState.isDirty}
            onSave={requestSave}
            onDiscard={discard}
            saving={saving}
          />,
        ]}
      />

      <PageContent>
        <FormProvider {...form}>
          <div className="space-y-6">
            <ErrorAlert error={error} />

            {!error && (
              <>
                {capacityMode === "advanced" ? (
                  // Advanced mode hides the concurrent limit, so its settings-search
                  // result lands on the mode toggle, where the owner switches back to Basic.
                  <div id="field-limits-concurrent-max">{modeSelector}</div>
                ) : (
                  modeSelector
                )}

                <DurationAwareToggle disabled={saving} />

                {capacityMode === "simple" ? (
                  <SimpleCapacityForm saving={saving} />
                ) : capacityMode === "advanced" ? (
                  <div className="space-y-6">
                    <div className="rounded-lg bg-muted p-4 text-sm text-muted-foreground space-y-2">
                      <p>{t("capacity.advancedIntro.description")}</p>
                      <p>{t("capacity.advancedIntro.example")}</p>
                      <p>{t("capacity.advancedIntro.noRuleDailyLimit")}</p>
                    </div>

                    <Card className="shadow-lg">
                      <CardContent className="p-6">
                        <DailyLimitField saving={saving} />
                      </CardContent>
                    </Card>

                    <TimeRangeLimits />
                  </div>
                ) : null}
              </>
            )}
          </div>

          {confirmLimits && (
            <BasicLimitsConfirmDialog
              onClose={() => setConfirmLimits(null)}
              limits={confirmLimits}
              onConfirm={save}
            />
          )}
        </FormProvider>
      </PageContent>
    </NavigationGuardProvider>
  );
}
